/* ---
  Artcontract worldview page.

  Port of about_artcontract_controller.js (Stimulus 1.x).
  Adapters: Custom Element instead of Stimulus, wheel deltaY, corrected
  breakpoint source swap on resize. Track timing and step flow are unchanged.
--- */

if (typeof AboutArtcontract !== 'function') {
  const ARTCONTRACT_VIDEO_TRACKS = [
    { s: 0, e: 9000 },
    { s: 9000, e: 13000 },
    { s: 13000, e: 26000 },
    { s: 26000, e: 39000 },
    { s: 39000, e: 49000 },
  ];

  class AboutArtcontract extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.hero = this.querySelector('[data-artcontract-el="hero"]');
      this.splash = this.querySelector('[data-artcontract-el="splash"]');
      this.loading = this.querySelector('[data-artcontract-el="loading"]');
      this.logo = this.querySelector('[data-artcontract-el="logo"]');
      this.videoPc = this.querySelector('[data-artcontract-el="video-pc"]');
      this.videoSp = this.querySelector('[data-artcontract-el="video-sp"]');
      this.sectionEls = this.querySelectorAll('[data-artcontract-el="section"]');
      this.stepEls = this.querySelectorAll('[data-artcontract-el="step"]');
      this.linkEls = this.querySelectorAll('[data-artcontract-el="link"]');

      this.dataset.trailing = this.dataset.trailing || 'false';
      this.dataset.step = this.dataset.step || '0';
      this.dataset.loaded = this.dataset.loaded || 'false';
      this.dataset.start = this.dataset.start || 'false';

      if (this.videoPc) this.dataset.pcSrc = this.videoPc.getAttribute('src') || '';
      if (this.videoSp) this.dataset.spSrc = this.videoSp.getAttribute('src') || '';

      this._onWheel = this.detectDirection.bind(this);
      this._onTouchMove = this.detectDirectionSp.bind(this);
      this._onResize = this.handleResize.bind(this);
      this._onHideHero = this.hideHero.bind(this);
      this._onStepClick = this.stepContainerHandleClick.bind(this);
      this._onNavClick = this.onClickLink.bind(this);
      this._onLoaded = this.getLoaded.bind(this);
      this._onPaused = this.getPaused.bind(this);
      this._onTimeupdate = this.getTimeupdate.bind(this);
      this._onMediaError = this.handleMediaError.bind(this);

      window.addEventListener('wheel', this._onWheel, { passive: true });
      window.addEventListener('touchmove', this._onTouchMove, { passive: true });
      window.addEventListener('resize', this._onResize);

      const hideHeroEl = this.querySelector('[data-artcontract-action="hide-hero"]');
      if (hideHeroEl) hideHeroEl.addEventListener('click', this._onHideHero);

      this.querySelectorAll('[data-artcontract-action="detect-direction"]').forEach((el) => {
        el.addEventListener('click', this._onWheel);
      });
      this.querySelectorAll('[data-artcontract-action="step-click"]').forEach((el) => {
        el.addEventListener('click', this._onStepClick);
      });
      this.querySelectorAll('[data-artcontract-action="nav-click"]').forEach((el) => {
        el.addEventListener('click', this._onNavClick);
      });

      [this.videoPc, this.videoSp].forEach((video) => {
        if (!video) return;
        video.addEventListener('loadedmetadata', this._onLoaded);
        video.addEventListener('loadeddata', this._onLoaded);
        video.addEventListener('canplay', this._onLoaded);
        video.addEventListener('error', this._onMediaError);
        video.addEventListener('pause', this._onPaused);
        video.addEventListener('timeupdate', this._onTimeupdate);
      });

      if (!this.dataset.pcSrc && !this.dataset.spSrc && this.logo) {
        this.logo.addEventListener('loadedmetadata', this._onLoaded);
        this.logo.addEventListener('error', this._onMediaError);
      }

      // 本番ページでは defer スクリプトが動く前に動画のメタデータ読み込みが
      // 完了していることがあり、その場合 loadedmetadata を待つと Loading が
      // 消えない。既に読み込み済みかをここで直接確認する。
      this.syncLoadedState();

      // preload が効かない環境（iOS Safari / 省データモードなど）では
      // 再生開始まで一切イベントが飛ばないため、最後の保険で解除する。
      this._loadFallbackTimer = window.setTimeout(() => {
        this.getLoaded();
      }, 8000);

      window.setTimeout(() => {
        this.applyBreakpointSources();
        this.syncLoadedState();
      }, 600);
    }

    disconnectedCallback() {
      window.removeEventListener('wheel', this._onWheel);
      window.removeEventListener('touchmove', this._onTouchMove);
      window.removeEventListener('resize', this._onResize);
      window.clearTimeout(this._loadFallbackTimer);
      window.clearTimeout(this._resizeTimer);
    }

    syncLoadedState() {
      if (this.toBoolean(this.dataset.loaded)) return;

      const candidates = [this.videoPc, this.videoSp];
      if (!this.dataset.pcSrc && !this.dataset.spSrc) candidates.push(this.logo);

      const ready = candidates.some((video) => {
        if (!video || !video.getAttribute('src')) return false;
        // HAVE_METADATA 以上、または読み込み失敗
        return video.readyState >= 1 || Boolean(video.error);
      });

      if (ready) this.getLoaded();
    }

    toBoolean(str) {
      return String(str).toLowerCase() === 'true';
    }

    applyBreakpointSources() {
      if (!this.videoPc || !this.videoSp) return;

      const pcSrc = this.dataset.pcSrc || '';
      const spSrc = this.dataset.spSrc || '';

      if (window.innerWidth < 768) {
        if (this.videoPc.getAttribute('src')) {
          this.videoPc.removeAttribute('src');
          this.videoPc.load();
        }
        if (spSrc && this.videoSp.getAttribute('src') !== spSrc) {
          this.videoSp.setAttribute('src', spSrc);
          this.videoSp.load();
        }
      } else {
        if (this.videoSp.getAttribute('src')) {
          this.videoSp.removeAttribute('src');
          this.videoSp.load();
        }
        if (pcSrc && this.videoPc.getAttribute('src') !== pcSrc) {
          this.videoPc.setAttribute('src', pcSrc);
          this.videoPc.load();
        }
      }
    }

    handleMediaError(e) {
      // src を外して load() した非表示側の動画は無視する。
      const target = e && e.currentTarget;
      if (target && !target.getAttribute('src')) return;
      this.getLoaded();
    }

    getLoaded() {
      if (this.toBoolean(this.dataset.loaded)) return;
      window.clearTimeout(this._loadFallbackTimer);

      this.dataset.loaded = 'true';
      if (this.loading) this.loading.classList.add('is-hide');
      if (this.logo) {
        this.logo.muted = true;
        this.logo.play();
      }
      window.setTimeout(() => {
        if (this.splash) this.splash.classList.add('is-back');
      }, 2000);
    }

    hideHero(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (this.toBoolean(this.dataset.loaded)) {
        Array.prototype.map.call(this.sectionEls, (ele) => {
          ele.classList.add('is-show');
        });
        if (this.hero) this.hero.classList.add('is-hide');
        this.dataset.start = 'true';
        if (window.innerWidth < 768) {
          this.nextTrackSp(0);
        } else {
          this.showStep(0);
          this.selectTrack(0);
        }
      }
    }

    detectDirection(e) {
      const isClick = e && e.type === 'click';
      const deltaY = e && typeof e.deltaY === 'number' ? e.deltaY : 0;

      if (
        this.toBoolean(this.dataset.start) &&
        this.videoPc &&
        this.videoPc.paused &&
        window.innerWidth > 768
      ) {
        if (isClick) {
          return this.toBoolean(this.dataset.trailing) ? null : this.nextStep();
        }
        if (deltaY < 0) {
          // return this.toBoolean(this.dataset.trailing) ? null : this.prevStep();
        } else if (deltaY > 0) {
          return this.toBoolean(this.dataset.trailing) ? null : this.nextStep();
        }
      } else if (this.toBoolean(this.dataset.loaded) && !this.toBoolean(this.dataset.start)) {
        if (!isClick && deltaY > 0) {
          window.setTimeout(this.hideHero.bind(this), 1000);
        }
      }
    }

    detectDirectionSp() {
      if (this.toBoolean(this.dataset.loaded) && !this.toBoolean(this.dataset.start)) {
        window.setTimeout(this.hideHero.bind(this), 1000);
      }
    }

    nextStep() {
      this.dataset.trailing = 'true';
      const step = parseInt(this.dataset.step, 10) > 3 ? 4 : parseInt(this.dataset.step, 10) + 1;
      this.hidePcArrowStep();

      if (step !== 4) {
        this.showStep(step);
        this.nextTrack(step);
      } else {
        this.showEndcontents();
      }
    }

    prevStep() {
      this.dataset.trailing = 'true';
      const step = parseInt(this.dataset.step, 10) < 1 ? 0 : parseInt(this.dataset.step, 10) - 1;
      this.showStep(step);
      this.selectTrack(step);
    }

    nextStepSp() {
      this.dataset.trailing = 'true';
      const step = parseInt(this.dataset.step, 10) > 3 ? 4 : parseInt(this.dataset.step, 10) + 1;
      this.dataset.step = String(step);
      if (step !== 4) {
        Array.prototype.map.call(this.stepEls, (item) => {
          item.classList.remove('is-show');
        });
        this.nextTrackSp(step);
      } else {
        this.showEndcontents();
      }
    }

    showEndcontents() {
      this.dataset.step = '4';
      Array.prototype.map.call(this.stepEls, (step) => {
        step.classList.remove('is-show');
      });

      const endNav = this.querySelector('.is-endcontents');
      if (endNav) endNav.classList.add('is-show');
      Array.prototype.map.call(this.linkEls, (link, i) => {
        link.classList.remove('is-current');
        if (i === 5) {
          link.classList.add('is-current');
        }
      });
      if (window.innerWidth < 768) {
        this.nextTrackSp(4);
      } else {
        this.nextTrack(4);
      }
    }

    showStep(_step) {
      this.dataset.step = String(_step);
      Array.prototype.map.call(this.stepEls, (step, i) => {
        step.classList.remove('is-show');
        if (i === _step) {
          window.setTimeout(() => {
            step.classList.add('is-show');
            window.setTimeout(() => {
              this.dataset.trailing = 'false';
            }, 800);
          }, 600);
        }
      });

      Array.prototype.map.call(this.linkEls, (link, i) => {
        link.classList.remove('is-current');
        if (i === _step) {
          link.classList.add('is-current');
        }
      });
    }

    showPcArrowStep(_step) {
      this.dataset.step = String(_step);
      Array.prototype.map.call(this.stepEls, (step, i) => {
        if (i === _step) {
          window.setTimeout(() => {
            step.classList.add('is-pcArrowShow');
            window.setTimeout(() => {
              this.dataset.trailing = 'false';
            }, 800);
          }, 600);
        }
      });
    }

    hidePcArrowStep(_step) {
      this.dataset.step = _step;
      Array.prototype.map.call(this.stepEls, (step) => {
        step.classList.remove('is-pcArrowShow');
      });
    }

    selectTrack(_step) {
      if (!this.videoPc || !ARTCONTRACT_VIDEO_TRACKS[_step]) return;
      const duration = ARTCONTRACT_VIDEO_TRACKS[_step].e - ARTCONTRACT_VIDEO_TRACKS[_step].s;
      this.videoPc.currentTime = ARTCONTRACT_VIDEO_TRACKS[_step].s / 1000;
      this.videoPc.play();
      window.setTimeout(() => {
        this.videoPc.pause();
      }, duration);
    }

    nextTrack() {
      if (this.videoPc) this.videoPc.play();
    }

    nextTrackSp() {
      if (this.videoSp) this.videoSp.play();
    }

    getPaused() {
      if (window.innerWidth < 769) {
        const step = parseInt(this.dataset.step, 10) > 3 ? 4 : parseInt(this.dataset.step, 10);
        if (step !== 4) {
          this.showStep(step);
        }
      } else {
        const step = parseInt(this.dataset.step, 10) > 3 ? 4 : parseInt(this.dataset.step, 10);
        if (step !== 4) {
          this.showPcArrowStep(step);
        }

        if (this.dataset.step === '3') {
          Array.prototype.map.call(this.linkEls, (link, i) => {
            link.classList.remove('is-current');
            if (i === 4) {
              link.classList.add('is-current');
            }
          });
        }
      }
    }

    getTimeupdate() {
      const stepIndex = parseInt(this.dataset.step, 10);
      const track = ARTCONTRACT_VIDEO_TRACKS[stepIndex];
      if (!track) return;

      if (window.innerWidth < 769) {
        if (this.videoSp && this.videoSp.currentTime > track.e / 1000) {
          this.videoSp.pause();
        }
      } else if (this.videoPc && this.videoPc.currentTime > track.e / 1000) {
        this.videoPc.pause();
      }
    }

    handleResize() {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(() => {
        this.applyBreakpointSources();
      }, 150);
    }

    stepContainerHandleClick() {
      if (window.innerWidth < 769 && !this.toBoolean(this.dataset.trailing)) {
        this.nextStepSp();
      }
    }

    onClickLink(e) {
      if (e) e.preventDefault();
      // FIXME: バグがあるので一時封印。
      // this.showStep(parseInt(e.target.dataset.to, 10));
      // this.selectTrack(parseInt(e.target.dataset.to, 10));
    }
  }

  customElements.define('about-artcontract', AboutArtcontract);
}
